import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { playSound } from "@/lib/sounds";
import { fireVictoryConfetti } from "@/components/gameplay/celebrations";
import { isWinningProgress, localSupertAward } from "@/lib/gamePlayMath";
import { computeVsOutcome } from "@/lib/duelOutcome";

/**
 * useVsRobotDuel — owns ALL VS-ROBOT-only state and the round resolver.
 *
 * The hook is a no-op (returns inert defaults + does not subscribe to any
 * effects) when `enabled` is false, so it can be called unconditionally
 * from GamePlay regardless of mode.
 *
 * Contract with the parent:
 *  - Shared refs (live in GamePlay because they are also used by VS HUMANO
 *    and the answer() handler): userClickRef, roundResolvedRef,
 *    roundStartRef, progressRef. Passed in via `sharedRefs`.
 *  - Setters used by the resolver to mutate GamePlay-owned state.
 *  - `animateSpin` to trigger the next-round spin animation.
 *
 * Returned state:
 *  - robotProgress (rendered in RaceBar + SupertBar)
 *  - vsrFloating, vsrRobotFx, vsrOutcome (transient UI feedback)
 *  - vsrMyRank, vsrShareOpen + setter (post-game share modal)
 *  - robotProgressRef, vsrFinishedRef (consumed by useVsRobotFinish)
 *  - setUserClickInfo, robotScheduledRef, robotTimerRef (mutated by answer())
 *  - resolveRound(robotInfo) — invoked from `answer()` on early-resolve
 *    AND from the internal round-deadline setTimeout.
 */
export function useVsRobotDuel({
  enabled,
  session,
  phase,
  spinning,
  activePosition,
  round,
  repeatsSoFar,
  sharedRefs,                  // { userClickRef, roundResolvedRef, roundStartRef, progressRef }
  setProgress,
  setStreak,
  setPhase,
  setBusy,
  setFeedback,
  setReveal,
  setRound,
  animateSpin,
}) {
  const [robotProgress, setRobotProgress] = useState([]);
  const [userClickInfo, setUserClickInfo] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_, setRoundResolved] = useState(false);
  const [vsrOutcome, setVsrOutcome] = useState(null);
  const [vsrMyRank, setVsrMyRank] = useState(null);
  const [vsrShareOpen, setVsrShareOpen] = useState(false);
  const [vsrFloating, setVsrFloating] = useState({ user: null, robot: null });
  const [vsrRobotFx, setVsrRobotFx] = useState(null);

  const robotTimerRef = useRef(null);
  const vsrFinishedRef = useRef(false);
  const robotScheduledRef = useRef(null);
  const robotProgressRef = useRef([]);

  // Keep ref in sync so setTimeout-fired resolvers read fresh values.
  useEffect(() => { robotProgressRef.current = robotProgress; }, [robotProgress]);

  // Resets all VS-ROBOT-specific state (called from beginGame and playAgain).
  const reset = () => {
    setRobotProgress([]);
    setUserClickInfo(null);
    setRoundResolved(false);
    setVsrOutcome(null);
    setVsrMyRank(null);
    setVsrShareOpen(false);
    vsrFinishedRef.current = false;
  };

  // ---- Round resolver ---------------------------------------------------
  const resolveRound = async (robotInfo) => {
    if (sharedRefs.roundResolvedRef.current) return;
    sharedRefs.roundResolvedRef.current = true;
    setRoundResolved(true);

    const u = sharedRefs.userClickRef.current; // may be null
    const { userGain, oppGain: robotGain, outcomeText, outcomeSound } = computeVsOutcome(
      u,
      robotInfo,
      { oppName: session.robot.name, oppPossessive: "o robot" }
    );

    // Enrich the feedback with `simultaneous` / `robotFirst` flags so the
    // <Options /> grid can render lighter/darker shades AND lock itself when
    // the robot answered first (Notas 1, 2 e 3 do brief do produto).
    // Configurable per-game via `Game.simultaneous_ms` (0..2000 ms, default 500).
    const SIMUL_MS = Math.max(0, Math.min(2000, Number(session?.game?.simultaneous_ms ?? 500)));
    const robotFirst = !u; // user never clicked before the robot resolved
    const simultaneous = !!u && Math.abs((u.time || 0) - (robotInfo?.time || 0)) < SIMUL_MS;
    if (robotFirst) {
      // Reveal the correct answer WITHOUT marking any user chosen — grid dims.
      setFeedback((prev) => ({
        correctIndex: robotInfo?.correctIndex ?? prev?.correctIndex ?? null,
        chosen: null,
        correct: false,
        robotFirst: true,
        simultaneous: false,
      }));
    } else {
      setFeedback((prev) => (prev ? { ...prev, simultaneous, robotFirst: false } : prev));
    }

    setStreak((s) => (userGain > robotGain ? s + 1 : 0));
    setVsrOutcome({ text: outcomeText });
    if (outcomeSound === "correct") playSound(session?.game?.correct_sound || "correct", session?.game?.correct_sound_volume);
    else if (outcomeSound === "wrong") playSound(session?.game?.wrong_sound || "wrong", session?.game?.wrong_sound_volume);

    // Award tokens LOCALLY via localSupertAward — mirrors backend
    // `supert_award` so we can advance IMMEDIATELY without waiting for
    // N serial /play/award round-trips. Server calls are still fired
    // fire-and-forget for stats/logs.
    let userProg = sharedRefs.progressRef.current.slice();
    let robotProg = robotProgressRef.current.slice();
    const userAwards = [];
    const robotAwards = [];
    for (let i = 0; i < userGain; i++) {
      const a = localSupertAward({
        progress: userProg,
        mode: session.game.mode || "normal",
        repeats_so_far: repeatsSoFar,
        max_repeated_letters: session.game.max_repeated_letters || 0,
      });
      userProg = a.progress;
      userAwards.push({ item: a.item, was_repeat: a.was_repeat });
      api.post("/play/award", {
        progress: sharedRefs.progressRef.current,
        mode: session.game.mode || "normal",
        repeats_so_far: repeatsSoFar,
        max_repeated_letters: session.game.max_repeated_letters || 0,
      }).catch(() => { /* stats only */ });
    }
    for (let i = 0; i < robotGain; i++) {
      const a = localSupertAward({
        progress: robotProg,
        mode: session.game.mode || "normal",
        repeats_so_far: 0,
        max_repeated_letters: session.game.max_repeated_letters || 0,
      });
      robotProg = a.progress;
      robotAwards.push({ item: a.item, was_repeat: a.was_repeat });
      api.post("/play/award", {
        progress: robotProgressRef.current,
        mode: session.game.mode || "normal",
        repeats_so_far: 0,
        max_repeated_letters: session.game.max_repeated_letters || 0,
      }).catch(() => { /* stats only */ });
    }
    setProgress(userProg);
    setRobotProgress(robotProg);

    if (robotGain > 0) setVsrRobotFx("bounce");
    else if (userGain > 0 && !robotInfo.correct) setVsrRobotFx("shake");
    setTimeout(() => setVsrRobotFx(null), 700);

    setVsrFloating({ user: userAwards, robot: robotAwards });
    const filterKeepers = (arr) =>
      arr && arr.length ? arr.filter((a) => a.item === "*" || !a.was_repeat) : arr;
    setTimeout(() => {
      setVsrFloating((cur) => ({
        user: filterKeepers(cur.user),
        robot: filterKeepers(cur.robot),
      }));
    }, 1000);
    setTimeout(() => setVsrFloating({ user: null, robot: null }), 2200);

    const userWon = isWinningProgress(userProg, session.game.mode, session.game.supert_count || 1);
    const robotWon = isWinningProgress(robotProg, session.game.mode, session.game.supert_count || 1);

    if (userWon || robotWon) {
      // End-of-game path — keep a brief pause so the celebration/outcome
      // is readable before switching to the result screen.
      setTimeout(() => {
        if (userWon && robotWon) {
          fireVictoryConfetti();
          playSound(session?.game?.victory_sound || "victory");
          setPhase("won"); setBusy(false);
        } else if (userWon) {
          fireVictoryConfetti();
          playSound(session?.game?.victory_sound || "victory");
          setPhase("won"); setBusy(false);
        } else {
          playSound(session?.game?.defeat_sound || "defeat");
          setPhase("lost"); setBusy(false);
        }
      }, 900);
    } else {
      // Continue to next round — configurable delay so the RED/GREEN feedback
      // on the answered option button is visible before the option grid
      // re-renders with the next question's options. Read from
      // `Game.round_delay_ms` (0..5000 ms). Critical in VS Robot where the
      // robot can flip the round very fast and clicks meant for question N
      // could land on question N+1 without this gate.
      const roundDelayMs = Math.max(0, Math.min(5000, Number(session?.game?.round_delay_ms ?? 100)));
      setTimeout(() => {
        setFeedback(null); setReveal(null);
        setRound((rr) => rr + 1);
        // (card sound is played inside animateSpin at the exact commit frame)
        animateSpin();
        setBusy(false);
      }, roundDelayMs);
    }
  };

  // ---- Effect: schedule robot timer at the start of each round ----------
  useEffect(() => {
    if (!enabled || phase !== "playing" || spinning || !activePosition || !session?.robot) return undefined;
    // Reset round signals (shared refs + local state)
    setUserClickInfo(null);
    sharedRefs.userClickRef.current = null;
    sharedRefs.roundResolvedRef.current = false;
    setRoundResolved(false);
    setVsrOutcome(null);
    sharedRefs.roundStartRef.current = performance.now();

    const r = session.robot;
    const robotTimeMs = (Number(r.speed_min) + Math.random() * (Number(r.speed_max) - Number(r.speed_min))) * 1000;
    const robotCorrect = Math.random() * 100 < Number(r.efficacy);
    // Capture the CORRECT answer index for the current question so, when the
    // robot fires FIRST, we can still reveal the correct option in the grid
    // (dimmed, since the user missed the race). `sharedRefs.activeQuestionRef`
    // is populated on every render by the parent GamePlay.
    const correctIndex = sharedRefs.activeQuestionRef?.current?.correct ?? null;
    robotScheduledRef.current = { time: robotTimeMs, correct: robotCorrect, correctIndex };
    if (robotTimerRef.current) clearTimeout(robotTimerRef.current);
    robotTimerRef.current = setTimeout(() => {
      resolveRound({ time: robotTimeMs, correct: robotCorrect, correctIndex });
    }, robotTimeMs);
    return () => { if (robotTimerRef.current) clearTimeout(robotTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, phase, spinning, activePosition, round, session?.robot?.id]);

  return {
    // State (rendered)
    robotProgress,
    vsrOutcome,
    vsrMyRank,
    vsrShareOpen,
    vsrFloating,
    vsrRobotFx,
    // Setters (used by `answer()` and useVsRobotFinish)
    setVsrMyRank,
    setVsrShareOpen,
    setUserClickInfo,
    userClickInfo,
    // Refs (consumed by `answer()`'s early-resolve + finish hook)
    robotProgressRef,
    robotScheduledRef,
    robotTimerRef,
    vsrFinishedRef,
    // Resolver (called from `answer()` early-resolve path)
    resolveRound,
    reset,
  };
}
